/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useMemo } from "react";
import { Dayjs } from "dayjs";
import { When } from "react-if";
import { Box, FlexContainer, PieChart, Text, theme } from "@nordcloud/gnui";
import { useCurrency } from "~/hooks";
import { periodDayJs } from "~/services/customers";
import { FormatMoney } from "~/tools";
import { usePercentageDifference } from "./usePercentageDifference";
import { formatNumber, getPieChartTextSize } from "./utils";

type DayToDayWidgetProps = {
  current: number;
  previous: number;
};

const formattedDate = (date: Dayjs): string => date.format("DD MMMM YYYY");

export function DayToDayWidget({ current, previous }: DayToDayWidgetProps) {
  const { currency } = useCurrency();

  const {
    percentageDifference,
    isFinite: showComparisonChart,
    progress,
    quantifier,
  } = usePercentageDifference(previous, current);

  const displayedDateRange = (subtractMonth = 0) => {
    const today = periodDayJs().format("DD");
    const enddate = formattedDate(
      periodDayJs().subtract(subtractMonth, "month")
    );

    if (today === "01") {
      return enddate;
    }

    return `01 - ${enddate}`;
  };

  const thisMonthDisplayDate = useMemo(() => displayedDateRange(0), []);
  const prevMonthDisplayDate = useMemo(() => displayedDateRange(1), []);

  const displayPercentageValue = formatNumber(percentageDifference);
  const pieChartTextSize = getPieChartTextSize(displayPercentageValue);

  return (
    <FlexContainer>
      <When condition={showComparisonChart}>
        <PieChart
          progress={progress}
          size={96}
          strokeWidth={15}
          color={quantifier === "less" ? "success" : "danger"}
          mr="1rem"
          data-testid="day-to-day-widget-diff-chart"
        >
          <Text
            className="chartValue"
            size={pieChartTextSize.value}
            tag="span"
            weight="medium"
          >
            {displayPercentageValue}
            <Text
              className="chartValue"
              tag="span"
              weight="medium"
              size={pieChartTextSize.symbol}
            >
              %
            </Text>
          </Text>
          <Text className="chartCaption" size="xs" tag="span">
            {quantifier}
          </Text>
        </PieChart>
      </When>
      <Box shadow="shadow00" p={0}>
        <Text size="sm" tag="div" color={theme.color.text.text02}>
          {thisMonthDisplayDate}
        </Text>
        <Text size="xxl" tag="div" weight="medium">
          <FormatMoney value={current} currency={currency} />
        </Text>
        <Text tag="div" weight="medium">
          <FormatMoney
            value={Math.abs(current - previous)}
            currency={currency}
          />
          <Text size="sm" tag="span" px={theme.spacing.spacing01}>
            {quantifier} than
          </Text>
          <Text size="sm" tag="span">
            {prevMonthDisplayDate}
          </Text>
        </Text>
      </Box>
    </FlexContainer>
  );
}
